/* eslint-disable @typescript-eslint/no-explicit-any */

import { useDispatch } from "react-redux";

import { Map } from "maplibre-gl";
import { FeatureCollection, Geometry } from "geojson";

import { DEFAULT_STEP } from "@/constants/dataset";
import { CATCHMENT_CENTROID_COLUMN } from "@/constants/map";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { updateDatasetStyle } from "@/store/slice/datasetSlice";
import { Dataset, DatasetAttributeSchema, DatasetStyle } from "@/types/dataset";
import { hasCustomCategoryAssignments, isOrdinalType } from "@/utils/dataset";
import { addLayer, ensureMapStyleReady } from "@/utils/map";
import {
  addCentroidSource,
  addPolygonAutoRangeLayers,
  addPolygonCategoryLayers,
  addPolygonCustomCategoryLayers,
  addPolygonCustomRangeLayers,
  addPolygonSingleStyleLayers,
  addPolygonSource
} from "@/utils/map/polygon";

import * as h3 from "h3-js";
import ngeohash from "ngeohash";

type useH3GeoHashLayersProps = {
  map: Map;
  prepareLayerStyles: (
    parsedPolygonData: FeatureCollection<Geometry>,
    dataset: Dataset,
    selectedCategory: DatasetAttributeSchema,
    step: number
  ) => { updatedStyle: DatasetStyle };
  handleFeatureClick: (layerId: string) => void;
  handleMovePositionToLayer: (polygonData: FeatureCollection<Geometry>) => void;
  handleUpdateSourceWithRank: (value: {
    datasetId: string;
    style: DatasetStyle;
    update: {
      isEnabled?: boolean;
      maxRanking?: number;
      category?: DatasetAttributeSchema;
    };
    sourceData?: FeatureCollection;
  }) => Promise<void>;
};

const getReferenceGeoAttributeName = (dataset: Dataset) =>
  dataset.data.schema?.find((attr) => attr.is_reference_geo && attr.is_spatial)
    ?.attribute_name;

function h3CountsToGeoJson(
  properties: Record<string, any>[],
  referenceGeo?: string
): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: properties
      ?.map((item) => {
        const h3Id =
          (referenceGeo && item[referenceGeo]) ||
          item["H3 ID"] ||
          item["Grid ID"];
        if (!h3Id) return null;
        return {
          type: "Feature",
          properties: { ...item },
          geometry: {
            type: "Polygon",
            coordinates: [h3.cellToBoundary(h3Id, true)]
          }
        };
      })
      .filter(Boolean) // Remove nulls
  } as GeoJSON.FeatureCollection;
}

export function geohashToGeoJson(
  properties: Record<string, any>[],
  referenceGeo?: string
): FeatureCollection {
  return {
    type: "FeatureCollection",
    features: properties?.map((item) => {
      const geohashId = (referenceGeo && item[referenceGeo]) || item["Grid ID"];
      const bbox = ngeohash.decode_bbox(geohashId); // [minLat, minLng, maxLat, maxLng]
      const [minLat, minLng, maxLat, maxLng] = bbox;
      const coordinates = [
        [
          [minLng, minLat],
          [maxLng, minLat],
          [maxLng, maxLat],
          [minLng, maxLat],
          [minLng, minLat]
        ]
      ];
      return {
        type: "Feature",
        properties: { ...item },
        geometry: {
          type: "Polygon",
          coordinates: coordinates
        }
      };
    })
  } as FeatureCollection;
}

export const useH3GeoHashLayers = ({
  map,
  prepareLayerStyles,
  handleFeatureClick,
  handleMovePositionToLayer,
  handleUpdateSourceWithRank
}: useH3GeoHashLayersProps) => {
  const dispatch = useDispatch();
  const {
    isNewUIDatasetConfigEnabled,
    isRankingLabelEnabled,
    isCoverageTypeEnabled,
    isCustomMapVisualizationLegendEnabled
  } = useAppFeatures();

  const handleAddH3GeohashLayer = (
    dataset: Dataset,
    polygonData: FeatureCollection<Geometry>,
    customId?: string
  ) => {
    // Defer until the map's style is ready, so getSource() below is safe.
    const retry = () => handleAddH3GeohashLayer(dataset, polygonData, customId);
    if (!ensureMapStyleReady(map, retry)) return;
    const sourceId = customId || dataset.data.id;
    if (map.getSource(sourceId)) return;

    const newLayer = {
      id: sourceId,
      type: dataset.properties.spatialType,
      sublayers: {
        fill: `${dataset.data.id}-fill`,
        outline: `${dataset.data.id}-outline`,
        outlineSelected: `${dataset.data.id}-outline-selected`,
        label: `${dataset.data.id}-label`,
        rank: `${dataset.data.id}-rank`
      }
    };

    const data = polygonData.features.map((feature: { properties: any }) => ({
      ...feature.properties
    }));

    const geojson =
      dataset.properties.spatialType === "H3"
        ? h3CountsToGeoJson(data, getReferenceGeoAttributeName(dataset))
        : geohashToGeoJson(data, getReferenceGeoAttributeName(dataset));

    const { updatedStyle } = prepareLayerStyles(
      polygonData,
      dataset,
      dataset.selectedCategory,
      dataset.style.colorSteps || DEFAULT_STEP
    );

    addPolygonSource(map, sourceId, geojson);

    const cellCentroids = addCentroidSource(
      map,
      `${sourceId}-centroid`,
      geojson
    );

    const isSingleStyle =
      isCustomMapVisualizationLegendEnabled &&
      dataset.style.colorScaleMethod === "simple";
    // Custom Ordinal only "takes effect" once values are assigned; with an empty
    // config it falls through to the default ordinal/quantile coloring below.
    const isCustomCategory =
      isCustomMapVisualizationLegendEnabled &&
      dataset.style.colorScaleMethod === "custom_ordinal" &&
      hasCustomCategoryAssignments(dataset.style.customCategoryConfig);
    // Custom Breaks: per-range fill + outline from the committed config.
    const isCustomRange =
      isCustomMapVisualizationLegendEnabled &&
      dataset.style.colorScaleMethod === "custom_breaks" &&
      !!dataset.style.customRangeConfig;

    if (isSingleStyle) {
      // Simple Color Scale: a single flat fill + outline for the whole layer.
      addPolygonSingleStyleLayers(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        newLayer,
        updatedStyle
      );
    } else if (isCustomCategory) {
      // Custom Ordinal: per-value fill + outline from the committed config.
      addPolygonCustomCategoryLayers(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        newLayer,
        updatedStyle,
        dataset.selectedCategory.attribute_name
      );
    } else if (isCustomRange) {
      // Custom Breaks: per-range fill + outline from the committed config.
      addPolygonCustomRangeLayers(
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        newLayer,
        updatedStyle,
        dataset.selectedCategory.attribute_name
      );
    } else {
      const isCategory = isOrdinalType(dataset.selectedCategory.data_type);

      addLayer({
        isNewUIDatasetConfigEnabled,
        isRankingLabelEnabled,
        map,
        layerData: newLayer,
        style: updatedStyle,
        selectedCategory: dataset.selectedCategory.attribute_name,
        isCategory,
        addCategoryFunc: addPolygonCategoryLayers,
        addAutoRangeFunc: addPolygonAutoRangeLayers
      });
    }

    // Centroid dot is drawn on click (useCatchmentRadius). Here we only pre-nudge
    // the rank text up so the dot won't cover it when it appears on the cell.
    if (isCoverageTypeEnabled) {
      const hasCatchmentCentroid = polygonData.features.some(
        (feature) =>
          typeof feature.properties?.[CATCHMENT_CENTROID_COLUMN] === "string"
      );
      const rankLayerId = newLayer.sublayers.rank;
      if (hasCatchmentCentroid && map.getLayer(rankLayerId)) {
        map.setLayoutProperty(rankLayerId, "text-anchor", "bottom");
        map.setLayoutProperty(rankLayerId, "text-offset", [0, -0.4]);
      }
    }

    handleUpdateSourceWithRank({
      datasetId: `${sourceId}-centroid`,
      style: updatedStyle,
      update: {},
      sourceData: cellCentroids ?? undefined
    });

    dispatch(
      updateDatasetStyle({ datasetId: dataset.data.id, style: updatedStyle })
    );

    handleFeatureClick(newLayer.sublayers.fill);

    if (dataset.properties.isVisible) handleMovePositionToLayer(geojson);
  };

  return {
    handleAddH3GeohashLayer
  };
};

export default useH3GeoHashLayers;
